import type { CheckoutService, CheckoutServiceParticipant, CheckoutServiceRoom } from '@/scopes/checkout/domain/types'

/** Special slot key for the blank "add new greenfee" editing row */
export const GOLF_NEW_SLOT_KEY = '__new__'

/** Domain model for the Golf (Greenfee) service */
export interface CheckoutGolfService extends CheckoutService {
  /** Rooms for participant grouping */
  rooms: CheckoutServiceRoom[]
  /** Participants eligible for golf, indexed by participant ID */
  participants: Record<string, CheckoutServiceParticipant>
  /** Available greenfee options indexed by unique option ID */
  options: Record<string, GolfServiceOption>
  /** Golf course locations indexed by location ID */
  locations: Record<string, GolfServiceLocation>
  /** The none/no-golf option ID */
  isNoneOption: string
  /** Current editing state for each greenfee slot, indexed by slot ID */
  editingSelectedValues: Record<string, GolfServiceSelectedValue>
  /** Saved confirmed selections for each greenfee slot, indexed by slot ID */
  selectedValues: Record<string, GolfServiceSelectedValue>
}

/** A single available greenfee option */
export interface GolfServiceOption {
  /** Unique option ID (built as code??date) */
  id: string
  /** Product code from the API */
  code: string
  /** Golf course location ID */
  locationId: string
  /** Number of holes */
  holes: number
  /** Whether a buggy is included */
  withBuggy: boolean
  /** Whether a buggy upgrade is available at this course */
  isBuggyEligible: boolean
  /** Date of play (ISO 8601 string) */
  date: string
  /** Tee time (e.g. "09:00") */
  time: string
  /** Price per person */
  price: number
  /** Human-readable display label */
  label: string
  /** Whether this is the sentinel "no golf" option */
  isNoneOption: boolean
  /** Participant IDs this option is available for */
  appliesToParticipants: string[]
  /** Buggy occupancy constraints — only present when the option includes a buggy */
  buggyOccupancy?: { min: number, max: number }
}

/** A golf course location */
export interface GolfServiceLocation {
  /** Location ID */
  id: string
  /** Golf course display name */
  name: string
  /** Available hole counts at this course (e.g. [9, 18]) */
  availableHoles: number[]
  /** Whether a buggy can be added at this course */
  isBuggyEligible: boolean
  /** Dates available at this course (ISO 8601 strings) */
  availableDates: string[]
  /** Participant IDs eligible for this location */
  eligibleParticipantIds: string[]
}

/** The current filter/selection state for a single greenfee slot */
export interface GolfServiceSelectedValue {
  /** Selected golf course location ID */
  locationId: string
  /** Selected date of play (ISO 8601 string) */
  date: string
  /** Selected number of holes */
  holes: number
  /** Whether a buggy is requested */
  withBuggy: boolean
  /** Participant IDs assigned to this greenfee slot */
  participantIds: string[]
  /** The selected option ID (empty string when none selected yet) */
  optionId: string
  time: string
}
